import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import SubSections from './SubSections';

const {
    schema: generatedSchema,
    uiSchema: generatedUiSchema,
    group: generatedGroup,
    dependencies: generatedDependencies,
} = customSchemas.GenerateAnalyticsSchema(['Selected Tab']);

const schema: JSONSchema7 = {
    title: 'Sub Sections',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        ...generatedSchema,
        subSectionsList: {
            type: 'array',
            title: 'Sub sections labels list',
            items: {
                type: 'object',
                title: 'Sub Section Labels',
                properties: {
                    sectionId: {
                        type: 'string',
                        title: 'Sub Section Id',
                        default: '',
                    },
                    label: {
                        type: 'string',
                        title: 'Sub Section Label',
                        default: '',
                    },
                    analyticsLabel: {
                        type: 'string',
                        title: 'Sub Section Label for analytics',
                        default: '',
                    },
                    addToggleLabels: {
                        type: 'boolean',
                        title: 'Add Toggle Labels',
                        default: false,
                    },
                },
                dependencies: {
                    addToggleLabels: {
                        oneOf: [
                            {
                                properties: {
                                    addToggleLabels: { enum: [true] },
                                    toggleSectionsList: {
                                        type: 'array',
                                        title: 'Toggle sections Labels',
                                        items: {
                                            type: 'string',
                                            default: '',
                                        },
                                        default: [],
                                    },
                                    defaultToggleSection: {
                                        type: 'string',
                                        default: '',
                                        title: 'Default Toggle Section',
                                    },
                                },
                                required: ['defaultToggleSection'],
                            },
                            {
                                properties: {
                                    addToggleLabels: { enum: [false] },
                                },
                            },
                        ],
                    },
                },
            },
        },
        defaultActiveSubSection: {
            type: 'number',
            title: 'Default active sub section',
            default: 1,
            enum: [1, 2, 3, 4, 5, 6, 7, 8, 9],
        },
        isRenderAsTabs: {
            type: 'boolean',
            title: 'Use subsections as tabs',
            default: false,
        },
        arrowIconType: {
            type: 'string',
            title: 'Arrow icon type for mobile responsive',
            enum: ['', 'FontAwesome icon', 'Image'],
        },
        isTransformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        lazyLoadSections: {
            type: 'boolean',
            title: 'Lazy load subsections',
            default: false,
        },
    },
    dependencies: {
        isTransformData: {
            oneOf: [
                {
                    properties: {
                        isTransformData: { enum: [true] },
                        functionToTransform: {
                            type: 'string',
                            description: 'Function to transform',
                            default: 'return (data) => { return data }',
                        },
                    },
                },
            ],
        },
        isRenderAsTabs: {
            oneOf: [
                {
                    properties: {
                        isRenderAsTabs: { enum: [true] },
                        preSelectTabWithUrl: {
                            type: 'boolean',
                            title: 'Add ability to pre select tab based on url hash',
                            default: false,
                        },
                    },
                },
                {
                    properties: {
                        isRenderAsTabs: { enum: [false] },
                    },
                },
            ],
        },
        arrowIconType: {
            oneOf: [
                {
                    properties: {
                        arrowIconType: { enum: ['FontAwesome icon'] },
                        arrowIconLeft: {
                            type: 'string',
                            title: 'Enter value for Arrow Icon Left',
                            default: 'fa-chevron-left',
                        },
                        arrowIconRight: {
                            type: 'string',
                            title: 'Enter value for Arrow Icon Right',
                            default: 'fa-chevron-right',
                        },
                    },
                },
                {
                    properties: {
                        arrowIconType: { enum: ['Image'] },
                        arrowImageLeft: {
                            type: 'string',
                            title: 'Enter url for Arrow Left Image',
                        },
                        arrowImageRight: {
                            type: 'string',
                            title: 'Enter url for Arrow Left Image',
                        },
                    },
                },
            ],
        },
        preSelectTabWithUrl: {
            oneOf: [
                {
                    properties: {
                        preSelectTabWithUrl: { enum: [true] },
                        useSectionId: {
                            type: 'boolean',
                            title: 'Use section id to preselect url tabs',
                            default: false,
                        },
                        disableUrl: {
                            type: 'boolean',
                            title: 'Url to not add section id or label to it',
                            default: false,
                        },
                    },
                },
                {
                    properties: {
                        preSelectTabWithUrl: { enum: [false] },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
        ...generatedDependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'subSectionsList',
            'defaultActiveSubSection',
            'isRenderAsTabs',
            'preSelectTabWithUrl',
            'useSectionId',
            'disableUrl',
            'arrowIconType',
            'arrowIconLeft',
            'arrowIconRight',
            'arrowImageLeft',
            'arrowImageRight',
            'lazyLoadSections',
        ],
        ...generatedGroup,
        'Transform Data': ['isTransformData', 'functionToTransform'],
    },
];

const SubSectionsWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        ...generatedUiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        isTransformData: { 'ui:widget': 'CustomCheckbox' },
        isRenderAsTabs: { 'ui:widget': 'CustomCheckbox' },
        arrowIconType: { 'ui:widget': 'CustomSelect' },
        arrowIconLeft: { 'ui:widget': 'CustomTextInput' },
        arrowIconRight: { 'ui:widget': 'CustomTextInput' },
        arrowImageLeft: {
            'ui:placeholder': 'https://',
            'ui:widget': 'MediaPicker',
            'ui:options': {
                isImagePicker: true,
            },
        },
        arrowImageRight: {
            'ui:placeholder': 'https://',
            'ui:widget': 'MediaPicker',
            'ui:options': {
                isImagePicker: true,
            },
        },
        defaultActiveSubSection: { 'ui:widget': 'CustomSelect' },
        preSelectTabWithUrl: { 'ui:widget': 'CustomCheckbox' },
        useSectionId: { 'ui:widget': 'CustomCheckbox' },
        disableUrl: { 'ui:widget': 'CustomCheckbox' },
        functionToTransform: { 'ui:widget': 'JSEditor' },
        subSectionsList: {
            items: {
                sectionId: { 'ui:widget': 'ResourcePicker' },
                label: { 'ui:widget': 'ResourcePicker' },
                analyticsLabel: { 'ui:widget': 'ResourcePicker' },
                addToggleLabels: { 'ui:widget': 'CustomCheckbox' },
                toggleSectionsList: {
                    items: { 'ui:widget': 'ResourcePicker' },
                },
                defaultToggleSection: { 'ui:widget': 'ResourcePicker' },
            },
        },
        lazyLoadSections: { 'ui:widget': 'CustomCheckbox' },
    },
    templateView: SubSections,
    id: 'SubSections',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default SubSectionsWidget;
